import { useRef, useCallback, useState } from "react";
import { hapticSuccess } from "../utils/haptics";

/**
 * ShareCard — generates a beautiful shareable card for PRs, workouts, and streaks.
 * Uses canvas to render a styled image that can be shared or copied.
 */
export default function ShareCard({ type, data, onClose }) {
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const generateImage = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext("2d");
    const w = 600;
    const h = 400;
    canvas.width = w;
    canvas.height = h;

    // Background gradient
    const grad = ctx.createLinearGradient(0, 0, w, h);
    grad.addColorStop(0, "#1C1C1C");
    grad.addColorStop(1, "#2a2a2a");
    ctx.fillStyle = grad;
    roundRect(ctx, 0, 0, w, h, 24);
    ctx.fill();

    // Accent line at top
    ctx.fillStyle = "#7EB8A4";
    ctx.fillRect(40, 36, 48, 3);

    // Branding
    ctx.fillStyle = "#7EB8A4";
    ctx.font = "500 13px Inter, sans-serif";
    ctx.fillText("ELEVATE", 40, 72);

    if (type === "pr") {
      // PR Share Card
      ctx.fillStyle = "rgba(255,255,255,0.4)";
      ctx.font = "500 11px Inter, sans-serif";
      ctx.letterSpacing = "2px";
      ctx.fillText("PERSONAL RECORD", 40, 110);

      ctx.fillStyle = "#FFFFFF";
      ctx.font = "300 36px Inter, sans-serif";
      ctx.fillText(data.exerciseName || "Exercise", 40, 165);

      ctx.font = "300 64px 'DM Mono', monospace";
      ctx.fillText(`${data.weight}`, 40, 255);

      ctx.fillStyle = "rgba(255,255,255,0.5)";
      ctx.font = "400 20px Inter, sans-serif";
      ctx.fillText(`lbs × ${data.reps} reps`, 40, 290);

      if (data.previousWeight) {
        const improvement = Math.round(((data.weight - data.previousWeight) / data.previousWeight) * 100);
        ctx.fillStyle = "#7EB8A4";
        ctx.font = "500 16px Inter, sans-serif";
        ctx.fillText(`+${improvement}% improvement`, 40, 340);
      }
    } else if (type === "workout") {
      // Workout Share Card
      ctx.fillStyle = "rgba(255,255,255,0.4)";
      ctx.font = "500 11px Inter, sans-serif";
      ctx.fillText("WORKOUT COMPLETE", 40, 110);

      ctx.fillStyle = "#FFFFFF";
      ctx.font = "300 32px Inter, sans-serif";
      ctx.fillText(data.name || "Workout", 40, 160);

      // Stats row
      const stats = [
        { value: `${data.duration || 0}`, label: "min" },
        { value: `${data.sets || 0}`, label: "sets" },
        { value: `${data.exercises || 0}`, label: "exercises" },
      ];

      stats.forEach((s, i) => {
        const x = 40 + i * 180;
        ctx.fillStyle = "#FFFFFF";
        ctx.font = "300 40px 'DM Mono', monospace";
        ctx.fillText(s.value, x, 240);
        ctx.fillStyle = "rgba(255,255,255,0.4)";
        ctx.font = "400 13px Inter, sans-serif";
        ctx.fillText(s.label, x, 265);
      });

      if (data.muscleGroups?.length) {
        ctx.fillStyle = "rgba(255,255,255,0.3)";
        ctx.font = "400 14px Inter, sans-serif";
        ctx.fillText(data.muscleGroups.join(" · "), 40, 340);
      }
    } else if (type === "week") {
      // Weekly review share card
      ctx.fillStyle = "rgba(255,255,255,0.4)";
      ctx.font = "500 11px Inter, sans-serif";
      ctx.fillText("MY WEEK IN TRAINING", 40, 110);

      const stats = [
        { value: `${data.sessions ?? 0}`, label: "sessions" },
        { value: `${data.prs ?? 0}`, label: "PRs" },
        { value: `${data.minutes ?? 0}`, label: "minutes" },
      ];
      stats.forEach((s, i) => {
        const x = 40 + i * 180;
        ctx.fillStyle = "#FFFFFF";
        ctx.font = "300 48px 'DM Mono', monospace";
        ctx.fillText(s.value, x, 210);
        ctx.fillStyle = "rgba(255,255,255,0.4)";
        ctx.font = "400 13px Inter, sans-serif";
        ctx.fillText(s.label, x, 238);
      });

      if (data.highlight) {
        ctx.fillStyle = "#7EB8A4";
        ctx.font = "400 16px Inter, sans-serif";
        // Wrap the highlight to two lines max
        const words = String(data.highlight).split(" ");
        let line = "";
        let y = 310;
        for (const word of words) {
          if (ctx.measureText(line + word).width > 520 && line) {
            ctx.fillText(line.trim(), 40, y);
            line = word + " ";
            y += 26;
            if (y > 336) break;
          } else {
            line += word + " ";
          }
        }
        if (y <= 336 && line.trim()) ctx.fillText(line.trim(), 40, y);
      }
    } else if (type === "streak") {
      // Streak Share Card
      ctx.fillStyle = "rgba(255,255,255,0.4)";
      ctx.font = "500 11px Inter, sans-serif";
      ctx.fillText("CONSISTENCY", 40, 110);

      ctx.fillStyle = "#FFFFFF";
      ctx.font = "300 80px 'DM Mono', monospace";
      ctx.fillText(`${data.days}`, 40, 220);

      ctx.fillStyle = "rgba(255,255,255,0.5)";
      ctx.font = "300 24px Inter, sans-serif";
      ctx.fillText("day streak", 40, 260);

      // Mini week dots
      if (data.weekData) {
        data.weekData.forEach((day, i) => {
          const x = 40 + i * 36;
          const y = 320;
          ctx.beginPath();
          ctx.arc(x + 12, y, 10, 0, Math.PI * 2);
          ctx.fillStyle = day.level > 0 ? "#7EB8A4" : "rgba(255,255,255,0.1)";
          ctx.fill();
          ctx.fillStyle = day.level > 0 ? "#fff" : "rgba(255,255,255,0.3)";
          ctx.font = "500 9px Inter, sans-serif";
          ctx.textAlign = "center";
          ctx.fillText(day.shortLabel, x + 12, y + 3);
          ctx.textAlign = "left";
        });
      }
    }

    // Date watermark
    ctx.fillStyle = "rgba(255,255,255,0.2)";
    ctx.font = "400 12px Inter, sans-serif";
    ctx.textAlign = "right";
    ctx.fillText(new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }), w - 40, h - 30);
    ctx.textAlign = "left";

    return canvas;
  }, [type, data]);

  const handleShare = useCallback(async () => {
    const canvas = generateImage();
    if (!canvas) return;

    hapticSuccess();

    try {
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));

      if (navigator.share && navigator.canShare?.({ files: [new File([blob], "elevate.png", { type: "image/png" })] })) {
        const file = new File([blob], "elevate.png", { type: "image/png" });
        await navigator.share({
          title: "Elevate",
          text: getShareText(type, data),
          files: [file],
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.write([
          new ClipboardItem({ "image/png": blob }),
        ]);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (e) {
      // User cancelled share or clipboard failed — try text fallback
      if (e.name !== "AbortError") {
        try {
          await navigator.clipboard.writeText(getShareText(type, data));
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          // Silently fail
        }
      }
    }
  }, [generateImage, type, data]);

  return (
    <div className="share-card-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="share-card-modal" style={{ animation: "slideUp 0.3s var(--ease), fadeIn 0.3s ease" }}>
        <div className="share-card-preview">
          <canvas
            ref={(el) => {
              canvasRef.current = el;
              if (el) generateImage();
            }}
            style={{ width: "100%", height: "auto", borderRadius: 16 }}
          />
        </div>

        <div className="share-card-actions">
          <button className="btn btn-primary btn-h44 w-full" onClick={handleShare}>
            {copied ? "Copied!" : navigator.share ? "Share" : "Copy Image"}
          </button>
          <button className="btn btn-ghost w-full" onClick={onClose} style={{ marginTop: 8, fontSize: 13 }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function getShareText(type, data) {
  if (type === "pr") {
    return `New PR on ${data.exerciseName}: ${data.weight} lbs × ${data.reps} reps 💪 #Elevate`;
  }
  if (type === "workout") {
    return `Just finished ${data.name} — ${data.duration} min, ${data.sets} sets 🏋️ #Elevate`;
  }
  if (type === "streak") {
    return `${data.days}-day streak and counting 🔥 #Elevate`;
  }
  if (type === "week") {
    return `My week: ${data.sessions ?? 0} sessions, ${data.prs ?? 0} PRs, ${data.minutes ?? 0} minutes trained 💪 #Elevate`;
  }
  return "Training with Elevate 💪";
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
